import { Helmet } from "react-helmet-async";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function AdminBillManagement() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [bills, setBills] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showBillModal, setShowBillModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Template settings
  const [templateSettings, setTemplateSettings] = useState({
    companyName: '',
    companyNameUrdu: '',
    businessType: 'Fruit Commission Agent',
    businessTypeUrdu: 'فروٹ کمیشن ایجنٹ',
    logo: '',
    logoType: 'link', // 'link' or 'upload'
    trademark: '',
    trademarkUrdu: 'ٹریڈ مارک',
    address: '',
    addressUrdu: '',
    contactInfo: [],
    headerBackground: 'gradient', // 'gradient', 'solid', 'image'
    headerBackgroundColor: '#4f46e5',
    headerBackgroundImage: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');

  // Dynamic fields - Set as per image requirements
  const [expenseFields, setExpenseFields] = useState([
    { fieldName: 'Vehicle', fieldNameUrdu: 'گاڑی', fieldType: 'number', isRequired: false, order: 0 },
    { fieldName: 'Cash', fieldNameUrdu: 'نقدی', fieldType: 'number', isRequired: false, order: 1 },
    { fieldName: 'Rent', fieldNameUrdu: 'کرایہ', fieldType: 'number', isRequired: false, order: 2 },
    { fieldName: 'Commission', fieldNameUrdu: 'کمیشن', fieldType: 'number', isRequired: false, order: 3 },
    { fieldName: 'Labor', fieldNameUrdu: 'مزدوری', fieldType: 'number', isRequired: false, order: 4 },
    { fieldName: 'Miscellaneous', fieldNameUrdu: 'مشیانہ', fieldType: 'number', isRequired: false, order: 5 },
    { fieldName: 'Market Fee', fieldNameUrdu: 'مارکیٹ فیس', fieldType: 'number', isRequired: false, order: 6 }
  ]);

  const [salesFields, setSalesFields] = useState([
    { fieldName: 'Raw Sale', fieldNameUrdu: 'خام بِکری', fieldType: 'number', isRequired: true, order: 0 },
    { fieldName: 'Expenses', fieldNameUrdu: 'اخراجات', fieldType: 'number', isRequired: false, order: 1 },
    { fieldName: 'Net Sale', fieldNameUrdu: 'پختہ بِکری', fieldType: 'number', isRequired: false, order: 2 }
  ]);

  // Bill form data
  const [billData, setBillData] = useState({
    serialNumber: '',
    customerName: '',
    customerNameUrdu: '',
    date: new Date().toLocaleDateString('en-CA'),
    receivedMessage: 'Received, thanks for selling',
    receivedMessageUrdu: 'گاڑی نمبر',
    goodsMessage: 'Your goods via consignment/bill of lading',
    goodsMessageUrdu: '',
    expenses: {},
    sales: {}
  });

  // Company language settings
  const [companyLanguage, setCompanyLanguage] = useState({
    language: 'en',
    websiteLanguage: 'en'
  });

  const getToken = () => localStorage.getItem("adminToken");

  // Memoize ReactQuill modules to prevent re-renders
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['clean']
    ]
  }), []);

  // Memoized onChange handlers to prevent infinite loops
  const handleReceivedMessageChange = useCallback((value) => {
    setBillData(prev => {
      if (prev.receivedMessage !== value) {
        return { ...prev, receivedMessage: value };
      }
      return prev;
    });
  }, []);

  const handleReceivedMessageUrduChange = useCallback((value) => {
    setBillData(prev => {
      if (prev.receivedMessageUrdu !== value) {
        return { ...prev, receivedMessageUrdu: value };
      }
      return prev;
    });
  }, []);

  const handleGoodsMessageChange = useCallback((value) => {
    setBillData(prev => {
      if (prev.goodsMessage !== value) {
        return { ...prev, goodsMessage: value };
      }
      return prev;
    });
  }, []);

  const handleGoodsMessageUrduChange = useCallback((value) => {
    setBillData(prev => {
      if (prev.goodsMessageUrdu !== value) {
        return { ...prev, goodsMessageUrdu: value };
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchCompanies();
  }, [navigate]);

  const fetchCompanies = async () => {
    try {
      const token = getToken();
      const response = await api.get("/admin/companies", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error("Fetch companies error:", error);
      toast.error("Failed to load companies");
    }
  };

  const fetchCompanyBills = async (companyId) => {
    try {
      setIsLoading(true);
      const token = getToken();
      const response = await api.get(`/bill/admin/company/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBills(response.data.bills || []);
    } catch (error) {
      console.error("Fetch bills error:", error);
      toast.error("Failed to load bills");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanySelect = async (companyId) => {
    const company = companies.find(c => c._id === companyId);
    setSelectedCompany(company);
    setCompanyLanguage({
      language: company?.language || 'en',
      websiteLanguage: company?.websiteLanguage || 'en'
    });
    await fetchCompanyBills(companyId);
    // Load template if exists
    loadTemplate(companyId);
  };

  const loadTemplate = async (companyId) => {
    try {
      const token = getToken();
      const response = await api.get(`/bill/admin/company/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Find draft template or use the most recent bill's template
      let template = response.data.bills?.find(b => b.status === 'draft');
      if (!template && response.data.bills?.length > 0) {
        // Use most recent bill's template structure
        template = response.data.bills[0];
      }
      if (template && template.templateSettings) {
        const settings = template.templateSettings;
        setTemplateSettings({
          companyName: settings.companyName || '',
          companyNameUrdu: settings.companyNameUrdu || '',
          businessType: settings.businessType || 'Fruit Commission Agent',
          businessTypeUrdu: settings.businessTypeUrdu || 'فروٹ کمیشن ایجنٹ',
          logo: settings.logo || '',
          logoType: settings.logo ? (settings.logo.startsWith('data:') ? 'upload' : 'link') : 'link',
          trademark: settings.trademark || '',
          trademarkUrdu: settings.trademarkUrdu || 'ٹریڈ مارک',
          address: settings.address || '',
          addressUrdu: settings.addressUrdu || '',
          contactInfo: settings.contactInfo || [],
          headerBackground: settings.headerBackground || 'gradient',
          headerBackgroundColor: settings.headerBackgroundColor || '#4f46e5',
          headerBackgroundImage: settings.headerBackgroundImage || ''
        });
        if (settings.logo && settings.logo.startsWith('data:')) {
          setLogoPreview(settings.logo);
        }
        if (template.expenseFields && template.expenseFields.length > 0) {
          setExpenseFields(template.expenseFields);
        }
        if (template.salesFields && template.salesFields.length > 0) {
          setSalesFields(template.salesFields);
        }
      } else {
        // Use default fields if no template exists
        const company = companies.find(c => c._id === companyId) || selectedCompany;
        setTemplateSettings({
          companyName: company?.shopName || company?.username || '',
          companyNameUrdu: company?.shopNameUrdu || '',
          businessType: 'Fruit Commission Agent',
          businessTypeUrdu: 'فروٹ کمیشن ایجنٹ',
          logo: company?.logo || '',
          logoType: company?.logo ? (company.logo.startsWith('data:') ? 'upload' : 'link') : 'link',
          trademark: '',
          trademarkUrdu: 'ٹریڈ مارک',
          address: company?.address || '',
          addressUrdu: company?.addressUrdu || '',
          contactInfo: (company?.persons || []).map(p => ({
            name: p.name,
            nameUrdu: p.nameUrdu || p.name,
            phone: p.contactNumber
          })),
          headerBackground: 'gradient',
          headerBackgroundColor: '#4f46e5',
          headerBackgroundImage: ''
        });
      }
    } catch (error) {
      console.error("Load template error:", error);
      // Set defaults from company data
      const company = companies.find(c => c._id === companyId) || selectedCompany;
      if (company) {
        setTemplateSettings({
          companyName: company.shopName || company.username || '',
          companyNameUrdu: company.shopNameUrdu || '',
          businessType: 'Fruit Commission Agent',
          businessTypeUrdu: 'فروٹ کمیشن ایجنٹ',
          logo: company.logo || '',
          logoType: company.logo ? (company.logo.startsWith('data:') ? 'upload' : 'link') : 'link',
          trademark: '',
          trademarkUrdu: 'ٹریڈ مارک',
          address: company.address || '',
          addressUrdu: company.addressUrdu || '',
          contactInfo: (company.persons || []).map(p => ({
            name: p.name,
            nameUrdu: p.nameUrdu || p.name,
            phone: p.contactNumber
          })),
          headerBackground: 'gradient',
          headerBackgroundColor: '#4f46e5',
          headerBackgroundImage: ''
        });
      }
    }
  };

  const handleAddExpenseField = () => {
    setExpenseFields([...expenseFields, {
      fieldName: '',
      fieldNameUrdu: '',
      fieldType: 'number',
      isRequired: false,
      order: expenseFields.length
    }]);
  };

  const handleRemoveExpenseField = (index) => {
    setExpenseFields(expenseFields.filter((_, i) => i !== index));
  };

  const handleUpdateExpenseField = (index, field) => {
    const updated = [...expenseFields];
    updated[index] = { ...updated[index], ...field };
    setExpenseFields(updated);
  };

  // Handler for expense field name changes with rich text
  const handleExpenseFieldNameChange = useCallback((index, value) => {
    setExpenseFields(prev => {
      const updated = [...prev];
      if (updated[index].fieldName !== value) {
        updated[index] = { ...updated[index], fieldName: value };
        return updated;
      }
      return prev;
    });
  }, []);

  // Handler for expense field name Urdu changes with rich text
  const handleExpenseFieldNameUrduChange = useCallback((index, value) => {
    setExpenseFields(prev => {
      const updated = [...prev];
      if (updated[index].fieldNameUrdu !== value) {
        updated[index] = { ...updated[index], fieldNameUrdu: value };
        return updated;
      }
      return prev;
    });
  }, []);

  // Handler for sales field name changes with rich text
  const handleSalesFieldNameChange = useCallback((index, value) => {
    setSalesFields(prev => {
      const updated = [...prev];
      if (updated[index].fieldName !== value) {
        updated[index] = { ...updated[index], fieldName: value };
        return updated;
      }
      return prev;
    });
  }, []);

  // Handler for sales field name Urdu changes with rich text
  const handleSalesFieldNameUrduChange = useCallback((index, value) => {
    setSalesFields(prev => {
      const updated = [...prev];
      if (updated[index].fieldNameUrdu !== value) {
        updated[index] = { ...updated[index], fieldNameUrdu: value };
        return updated;
      }
      return prev;
    });
  }, []);

  const handleAddSalesField = () => {
    setSalesFields([...salesFields, {
      fieldName: '',
      fieldNameUrdu: '',
      fieldType: 'number',
      isRequired: false,
      order: salesFields.length
    }]);
  };

  const handleRemoveSalesField = (index) => {
    setSalesFields(salesFields.filter((_, i) => i !== index));
  };

  const handleUpdateSalesField = (index, field) => {
    const updated = [...salesFields];
    updated[index] = { ...updated[index], ...field };
    setSalesFields(updated);
  };

  const handleSaveTemplate = async () => {
    if (!selectedCompany) {
      toast.error("Please select a company first");
      return;
    }

    try {
      setIsLoading(true);
      const token = getToken();
      await api.post("/bill/admin/template", {
        companyId: selectedCompany._id,
        templateSettings,
        expenseFields,
        salesFields
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Template saved successfully!");
      setShowTemplateModal(false);
    } catch (error) {
      console.error("Save template error:", error);
      toast.error(error.response?.data?.message || "Failed to save template");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditBill = (bill) => {
    setBillData({
      serialNumber: bill.serialNumber || '',
      customerName: bill.customerName || '',
      customerNameUrdu: bill.customerNameUrdu || '',
      date: bill.date ? new Date(bill.date).toLocaleDateString('en-CA') : new Date().toLocaleDateString('en-CA'),
      receivedMessage: bill.receivedMessage || 'Received, thanks for selling',
      receivedMessageUrdu: bill.receivedMessageUrdu || 'گاڑی نمبر',
      goodsMessage: bill.goodsMessage || 'Your goods via consignment/bill of lading',
      goodsMessageUrdu: bill.goodsMessageUrdu || '',
      expenses: bill.expenses || {},
      sales: bill.sales || {}
    });
    setShowBillModal(true);
    setEditingBill(bill);
  };

  const handleDeleteBill = async (billId) => {
    if (!window.confirm('Are you sure you want to delete this bill?')) {
      return;
    }

    try {
      setIsLoading(true);
      const token = getToken();
      await api.delete(`/bill/admin/${billId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Bill deleted successfully!");
      if (selectedCompany) {
        await fetchCompanyBills(selectedCompany._id);
      }
    } catch (error) {
      console.error("Delete bill error:", error);
      toast.error(error.response?.data?.message || "Failed to delete bill");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateBill = async () => {
    if (!selectedCompany) {
      toast.error("Please select a company first");
      return;
    }

    // Initialize expenses and sales with template fields
    const initializedExpenses = {};
    expenseFields.forEach(field => {
      if (field.fieldName) {
        initializedExpenses[field.fieldName] = billData.expenses[field.fieldName] || 0;
      }
    });

    const initializedSales = {};
    salesFields.forEach(field => {
      if (field.fieldName) {
        initializedSales[field.fieldName] = billData.sales[field.fieldName] || 0;
      }
    });

    try {
      setIsLoading(true);
      const token = getToken();
      const response = await api.post("/bill/admin/create", {
        companyId: selectedCompany._id,
        serialNumber: billData.serialNumber,
        customerName: billData.customerName,
        customerNameUrdu: billData.customerNameUrdu,
        date: billData.date,
        receivedMessage: billData.receivedMessage || 'Received, thanks for selling',
        receivedMessageUrdu: billData.receivedMessageUrdu || 'گاڑی نمبر',
        goodsMessage: billData.goodsMessage || 'Your goods via consignment/bill of lading',
        goodsMessageUrdu: billData.goodsMessageUrdu || '',
        expenses: initializedExpenses,
        sales: initializedSales,
        expenseFields: expenseFields,
        salesFields: salesFields
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bill created successfully!");
      setShowBillModal(false);
      setBillData({
        serialNumber: '',
        customerName: '',
        customerNameUrdu: '',
        date: new Date().toLocaleDateString('en-CA'),
        expenses: {},
        sales: {}
      });
      await fetchCompanyBills(selectedCompany._id);
    } catch (error) {
      console.error("Create bill error:", error);
      toast.error(error.response?.data?.message || "Failed to create bill");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateLanguage = async (type, value) => {
    if (!selectedCompany) return;

    try {
      const token = getToken();
      await api.put(`/admin/companies/${selectedCompany._id}`, {
        [type]: value
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyLanguage({ ...companyLanguage, [type]: value });
      toast.success("Language preference updated!");
      fetchCompanies();
    } catch (error) {
      console.error("Update language error:", error);
      toast.error("Failed to update language");
    }
  };

  const handleAdminLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    toast.info("Logged out from Admin panel");
    navigate("/admin/login");
  };

  return (
    <>
      <Helmet><title>Bill Management - Admin Panel</title></Helmet>

      <div className="min-h-screen bg-brand-background">
        {/* Mobile Menu Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        <div className="flex flex-col md:flex-row">
          {/* Sidebar - Fixed */}
          <aside className={`fixed top-0 left-0 h-screen w-64 bg-white shadow-md border-r border-brand-card/60 z-40 overflow-y-auto transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
            }`}>
            <div className="p-4 border-b border-brand-card/40 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-brand-header">Admin Navigation</h2>
                <p className="text-xs text-gray-500 mt-1">Quick access to tools</p>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="md:hidden text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="p-3 space-y-2 text-sm">
              <button
                onClick={() => {
                  navigate('/admin');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Dashboard</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/bills');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Bill Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/fields');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Field Settings</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/roznamacha-fields');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Roz Namacha Fields</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/recoveries');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Recovery Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/expenses');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Expense Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/payment-methods');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Payment Methods</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/team-members');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Team Members</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/recognitions');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Recognitions</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/blogs');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Blogs</span>
              </button>
              <button
                onClick={() => {
                  navigate('/company/dashboard');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Company Dashboard</span>
              </button>
              <button
                onClick={() => {
                  handleAdminLogout();
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Logout</span>
              </button>
            </nav>
          </aside>

          {/* Main Content */}
          <div className="flex-1 md:ml-64 p-4 md:p-8">
            <div className="max-w-7xl mx-auto">
              {/* Mobile Header with Menu Button */}
              <div className="md:hidden mb-4 flex items-center justify-between bg-white rounded-lg shadow-md p-4">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <h1 className="text-xl font-bold text-gray-900">Bill Management</h1>
                <div className="w-6"></div>
              </div>

              {/* Header */}
              <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Bill Management</h1>
                    <p className="text-gray-600 mt-1 text-sm md:text-base">Manage bills and templates for companies</p>
                  </div>
                  <div className="flex gap-3 w-full md:w-auto">
                    <button
                      onClick={() => navigate("/admin/fields")}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-3 md:px-4 py-2 rounded-lg transition text-sm md:text-base"
                    >
                      🎯 Field Management
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Left Sidebar - Company Selection */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Select Company</h2>
                  <select
                    onChange={(e) => handleCompanySelect(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 mb-4"
                  >
                    <option value="">-- Select Company --</option>
                    {companies.map((company) => (
                      <option key={company._id} value={company._id}>
                        {company.shopName || company.username} ({company.email})
                      </option>
                    ))}
                  </select>

                  {selectedCompany && (
                    <div className="space-y-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-2">Company Info</h3>
                        <p className="text-sm text-gray-600">Name: {selectedCompany.shopName || selectedCompany.username}</p>
                        <p className="text-sm text-gray-600">Email: {selectedCompany.email}</p>
                      </div>

                      {/* Language Settings */}
                      <div className="p-4 bg-indigo-50 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-3">Language Settings</h3>

                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Dashboard Language
                          </label>
                          <select
                            value={companyLanguage.language}
                            onChange={(e) => handleUpdateLanguage('language', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="en">English</option>
                            <option value="ur">Urdu</option>
                            <option value="both">Both (English + Urdu)</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Website Language
                          </label>
                          <select
                            value={companyLanguage.websiteLanguage}
                            onChange={(e) => handleUpdateLanguage('websiteLanguage', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="en">English</option>
                            <option value="ur">Urdu</option>
                            <option value="both">Both (English + Urdu)</option>
                          </select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <button
                          onClick={() => setShowTemplateModal(true)}
                          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded-lg transition"
                        >
                          Manage Bill Template
                        </button>
                        <button
                          onClick={() => {
                            // Initialize bill data with default fields
                            const defaultExpenses = {};
                            expenseFields.forEach(field => {
                              if (field.fieldName) {
                                defaultExpenses[field.fieldName] = 0;
                              }
                            });

                            const defaultSales = {};
                            salesFields.forEach(field => {
                              if (field.fieldName) {
                                defaultSales[field.fieldName] = 0;
                              }
                            });

                            setBillData({
                              serialNumber: '',
                              customerName: '',
                              customerNameUrdu: '',
                              date: new Date().toLocaleDateString('en-CA'),
                              receivedMessage: 'Received, thanks for selling',
                              receivedMessageUrdu: 'گاڑی نمبر',
                              goodsMessage: 'Your goods via consignment/bill of lading',
                              goodsMessageUrdu: '',
                              expenses: defaultExpenses,
                              sales: defaultSales
                            });
                            setEditingBill(null);
                            setShowBillModal(true);
                          }}
                          className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg transition"
                        >
                          Create New Bill
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Side - Bills List */}
                <div className="md:col-span-2 bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    Bills {selectedCompany && `- ${selectedCompany.shopName || selectedCompany.username}`}
                  </h2>

                  {!selectedCompany ? (
                    <div className="text-center py-12 text-gray-500">
                      <p>Please select a company to view bills</p>
                    </div>
                  ) : isLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    </div>
                  ) : bills.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p>No bills found. Create your first bill!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {bills.map((bill) => (
                        <div key={bill._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                Bill #{bill.serialNumber || bill._id.slice(-6)}
                              </h3>
                              <p className="text-sm text-gray-600">
                                Customer: {bill.customerName || bill.customerNameUrdu || 'N/A'}
                              </p>
                              <p className="text-sm text-gray-600">
                                Date: {new Date(bill.date).toLocaleDateString()}
                              </p>
                              <p className="text-sm font-semibold text-green-600 mt-2">
                                Net Sale: Rs. {bill.netSale || 0}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => window.open(`/bill/view/${bill._id}`, '_blank')}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-3 py-1 rounded text-sm"
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleEditBill(bill)}
                                className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold px-3 py-1 rounded text-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteBill(bill._id)}
                                className="bg-red-600 hover:bg-red-700 text-white font-semibold px-3 py-1 rounded text-sm"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Template Modal */}
        {showTemplateModal && selectedCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Bill Template Settings</h2>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Template Settings */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company Name (English)</label>
                      <input
                        type="text"
                        value={templateSettings.companyName}
                        onChange={(e) => setTemplateSettings({ ...templateSettings, companyName: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="Company Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company Name (Urdu)</label>
                      <input
                        type="text"
                        value={templateSettings.companyNameUrdu}
                        onChange={(e) => setTemplateSettings({ ...templateSettings, companyNameUrdu: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="کمپنی کا نام"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                      <div className="flex gap-4 mb-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="logoType"
                            value="link"
                            checked={templateSettings.logoType === 'link'}
                            onChange={(e) => setTemplateSettings({ ...templateSettings, logoType: 'link' })}
                          />
                          <span>Use Link</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="logoType"
                            value="upload"
                            checked={templateSettings.logoType === 'upload'}
                            onChange={(e) => setTemplateSettings({ ...templateSettings, logoType: 'upload' })}
                          />
                          <span>Upload Image</span>
                        </label>
                      </div>
                      {templateSettings.logoType === 'link' ? (
                        <input
                          type="url"
                          value={templateSettings.logo}
                          onChange={(e) => setTemplateSettings({ ...templateSettings, logo: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          placeholder="https://example.com/logobest.png"
                        />
                      ) : (
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setLogoFile(file);
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  const base64String = reader.result;
                                  setLogoPreview(base64String);
                                  setTemplateSettings({ ...templateSettings, logo: base64String });
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          />
                          {logoPreview && (
                            <div className="mt-2">
                              <img src={logoPreview} alt="Logo preview" className="h-20 w-20 object-contain border border-gray-300 rounded" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Header Background Image (Optional - for top box)</label>
                      <div className="flex gap-4 mb-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="headerBgType"
                            value="gradient"
                            checked={templateSettings.headerBackground === 'gradient'}
                            onChange={(e) => setTemplateSettings({ ...templateSettings, headerBackground: 'gradient' })}
                          />
                          <span>Gradient</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="headerBgType"
                            value="solid"
                            checked={templateSettings.headerBackground === 'solid'}
                            onChange={(e) => setTemplateSettings({ ...templateSettings, headerBackground: 'solid' })}
                          />
                          <span>Solid Color</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="headerBgType"
                            value="image"
                            checked={templateSettings.headerBackground === 'image'}
                            onChange={(e) => setTemplateSettings({ ...templateSettings, headerBackground: 'image' })}
                          />
                          <span>Image</span>
                        </label>
                      </div>
                      {templateSettings.headerBackground === 'solid' && (
                        <input
                          type="color"
                          value={templateSettings.headerBackgroundColor}
                          onChange={(e) => setTemplateSettings({ ...templateSettings, headerBackgroundColor: e.target.value })}
                          className="w-full h-12 border border-gray-300 rounded-lg"
                        />
                      )}
                      {templateSettings.headerBackground === 'image' && (
                        <div>
                          <input
                            type="url"
                            value={templateSettings.headerBackgroundImage}
                            onChange={(e) => setTemplateSettings({ ...templateSettings, headerBackgroundImage: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-2"
                            placeholder="https://example.com/background-image.jpg"
                          />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  const base64String = reader.result;
                                  setTemplateSettings({ ...templateSettings, headerBackgroundImage: base64String });
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          />
                          {templateSettings.headerBackgroundImage && (
                            <div className="mt-2">
                              <img src={templateSettings.headerBackgroundImage} alt="Background preview" className="h-32 w-full object-cover border border-gray-300 rounded" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Trademark (English)</label>
                      <input
                        type="text"
                        value={templateSettings.trademark}
                        onChange={(e) => setTemplateSettings({ ...templateSettings, trademark: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="AS"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Trademark (Urdu) / ٹریڈ مارک</label>
                      <input
                        type="text"
                        value={templateSettings.trademarkUrdu}
                        onChange={(e) => setTemplateSettings({ ...templateSettings, trademarkUrdu: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="ٹریڈ مارک"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Business Type (English)</label>
                      <input
                        type="text"
                        value={templateSettings.businessType}
                        onChange={(e) => setTemplateSettings({ ...templateSettings, businessType: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="Fruit Commission Agent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Business Type (Urdu)</label>
                      <input
                        type="text"
                        value={templateSettings.businessTypeUrdu}
                        onChange={(e) => setTemplateSettings({ ...templateSettings, businessTypeUrdu: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="فروٹ کمیشن ایجنٹ"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Address (English)</label>
                      <input
                        type="text"
                        value={templateSettings.address}
                        onChange={(e) => setTemplateSettings({ ...templateSettings, address: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="Address"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Address (Urdu)</label>
                      <input
                        type="text"
                        value={templateSettings.addressUrdu}
                        onChange={(e) => setTemplateSettings({ ...templateSettings, addressUrdu: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="پتہ"
                      />
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="mt-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-gray-700">Contact Information</h4>
                      <button
                        type="button"
                        onClick={() => {
                          setTemplateSettings({
                            ...templateSettings,
                            contactInfo: [...(templateSettings.contactInfo || []), { name: '', nameUrdu: '', phone: '' }]
                          });
                        }}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded text-sm"
                      >
                        + Add Contact
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(templateSettings.contactInfo || []).map((contact, idx) => (
                        <div key={idx} className="flex gap-2 items-end p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1 grid grid-cols-3 gap-2">
                            <input
                              type="text"
                              value={contact.name}
                              onChange={(e) => {
                                const updated = [...(templateSettings.contactInfo || [])];
                                updated[idx].name = e.target.value;
                                setTemplateSettings({ ...templateSettings, contactInfo: updated });
                              }}
                              placeholder="Name (English)"
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <input
                              type="text"
                              value={contact.nameUrdu}
                              onChange={(e) => {
                                const updated = [...(templateSettings.contactInfo || [])];
                                updated[idx].nameUrdu = e.target.value;
                                setTemplateSettings({ ...templateSettings, contactInfo: updated });
                              }}
                              placeholder="Name (Urdu)"
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <input
                              type="tel"
                              value={contact.phone}
                              onChange={(e) => {
                                const updated = [...(templateSettings.contactInfo || [])];
                                updated[idx].phone = e.target.value;
                                setTemplateSettings({ ...templateSettings, contactInfo: updated });
                              }}
                              placeholder="Phone"
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (templateSettings.contactInfo || []).filter((_, i) => i !== idx);
                              setTemplateSettings({ ...templateSettings, contactInfo: updated });
                            }}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Expense Fields */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Expense Fields</h3>
                    <button
                      onClick={handleAddExpenseField}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm"
                    >
                      + Add Field
                    </button>
                  </div>
                  <div className="space-y-3">
                    {expenseFields.map((field, index) => (
                      <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Field Name (English)</label>
                          <ReactQuill
                            theme="snow"
                            value={field.fieldName || ''}
                            onChange={(value) => handleExpenseFieldNameChange(index, value)}
                            placeholder="Field Name (English)"
                            modules={quillModules}
                            style={{ minHeight: '80px', marginBottom: '10px' }}
                          />
                          <label className="block text-xs font-medium text-gray-600 mb-1 mt-2">Field Name (Urdu)</label>
                          <ReactQuill
                            theme="snow"
                            value={field.fieldNameUrdu || ''}
                            onChange={(value) => handleExpenseFieldNameUrduChange(index, value)}
                            placeholder="Field Name (Urdu)"
                            modules={quillModules}
                            style={{ minHeight: '80px' }}
                          />
                        </div>
                        <button
                          onClick={() => handleRemoveExpenseField(index)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm self-start mt-8"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sales Fields */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Sales Fields</h3>
                    <button
                      onClick={handleAddSalesField}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm"
                    >
                      + Add Field
                    </button>
                  </div>
                  <div className="space-y-3">
                    {salesFields.map((field, index) => (
                      <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Field Name (English)</label>
                          <ReactQuill
                            theme="snow"
                            value={field.fieldName || ''}
                            onChange={(value) => handleSalesFieldNameChange(index, value)}
                            placeholder="Field Name (English)"
                            modules={quillModules}
                            style={{ minHeight: '80px', marginBottom: '10px' }}
                          />
                          <label className="block text-xs font-medium text-gray-600 mb-1 mt-2">Field Name (Urdu)</label>
                          <ReactQuill
                            theme="snow"
                            value={field.fieldNameUrdu || ''}
                            onChange={(value) => handleSalesFieldNameUrduChange(index, value)}
                            placeholder="Field Name (Urdu)"
                            modules={quillModules}
                            style={{ minHeight: '80px' }}
                          />
                        </div>
                        <button
                          onClick={() => handleRemoveSalesField(index)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm self-start mt-8"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => setShowTemplateModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveTemplate}
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
                  >
                    {isLoading ? "Saving..." : "Save Template"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Bill Modal */}
        {showBillModal && selectedCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingBill ? 'Edit Bill' : 'Create New Bill'}
                </h2>
                <button
                  onClick={() => {
                    setShowBillModal(false);
                    setEditingBill(null);
                    setBillData({
                      serialNumber: '',
                      customerName: '',
                      customerNameUrdu: '',
                      date: new Date().toLocaleDateString('en-CA'),
                      receivedMessage: 'Received, thanks for selling',
                      receivedMessageUrdu: 'گاڑی نمبر',
                      goodsMessage: 'Your goods via consignment/bill of lading',
                      goodsMessageUrdu: '',
                      expenses: {},
                      sales: {}
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Serial Number</label>
                    <input
                      type="text"
                      value={billData.serialNumber}
                      onChange={(e) => setBillData({ ...billData, serialNumber: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                    <input
                      type="date"
                      value={billData.date}
                      onChange={(e) => setBillData({ ...billData, date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name (English)</label>
                    <input
                      type="text"
                      value={billData.customerName}
                      onChange={(e) => setBillData({ ...billData, customerName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Customer Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name (Urdu)</label>
                    <input
                      type="text"
                      value={billData.customerNameUrdu}
                      onChange={(e) => setBillData({ ...billData, customerNameUrdu: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="کسٹمر کا نام"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Received Message (English)</label>
                    <ReactQuill
                      theme="snow"
                      value={billData.receivedMessage || ''}
                      onChange={handleReceivedMessageChange}
                      placeholder="Received, thanks for selling"
                      modules={quillModules}
                      style={{ minHeight: '150px', marginBottom: '50px' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Goods Message (English)</label>
                    <ReactQuill
                      theme="snow"
                      value={billData.goodsMessage || ''}
                      onChange={handleGoodsMessageChange}
                      placeholder="Your goods via consignment/bill of lading"
                      modules={quillModules}
                      style={{ minHeight: '150px', marginBottom: '50px' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Goods Message (Urdu) / گاڑی نمبر</label>
                    <ReactQuill
                      theme="snow"
                      value={billData.goodsMessageUrdu || ''}
                      onChange={handleGoodsMessageUrduChange}
                      placeholder=""
                      modules={quillModules}
                      style={{ minHeight: '150px', marginBottom: '50px' }}
                    />
                  </div>
                </div>

                {/* Expense Fields */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Expenses</h3>
                  <div className="space-y-3">
                    {expenseFields.map((field, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <label className="w-32 text-sm text-gray-700">
                          {field.fieldName} ({field.fieldNameUrdu})
                        </label>
                        <input
                          type="number"
                          value={billData.expenses[field.fieldName] || ''}
                          onChange={(e) => setBillData({
                            ...billData,
                            expenses: {
                              ...billData.expenses,
                              [field.fieldName]: parseFloat(e.target.value) || 0
                            }
                          })}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                          placeholder="0"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sales Fields */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Sales</h3>
                  <div className="space-y-3">
                    {salesFields.map((field, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <label className="w-32 text-sm text-gray-700">
                          {field.fieldName} ({field.fieldNameUrdu})
                        </label>
                        <input
                          type="number"
                          value={billData.sales[field.fieldName] || ''}
                          onChange={(e) => setBillData({
                            ...billData,
                            sales: {
                              ...billData.sales,
                              [field.fieldName]: parseFloat(e.target.value) || 0
                            }
                          })}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                          placeholder="0"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => setShowBillModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateBill}
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
                  >
                    {isLoading ? (editingBill ? "Updating..." : "Creating...") : (editingBill ? "Update Bill" : "Create Bill")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AdminBillManagement;

